import { useEffect, useState } from 'react';
import { Alert, Button, Modal, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';

const STYLE = `
.packing-slip { background:#fff; color:#111; padding:18px; font-size:13px; line-height:1.4; border:1px solid #222; max-width:760px; margin:0 auto; }
.packing-slip h2 { font-size:18px; letter-spacing:.06em; margin:0 0 2px; }
.packing-slip table { width:100%; border-collapse:collapse; color:#111; }
.packing-slip th, .packing-slip td { border:1px solid #444; padding:4px 6px; vertical-align:top; }
.packing-slip th { background:#f1f1f1; }
.packing-slip .ps-label { font-size:10.5px; text-transform:uppercase; letter-spacing:.04em; color:#555; }
.packing-slip .ps-cod { border:2px solid #111; padding:6px 10px; font-weight:700; font-size:15px; margin:10px 0; }
.packing-slip .ps-box { display:inline-block; width:14px; height:14px; border:1.5px solid #111; vertical-align:middle; }
@media print { .packing-slip { border:none; max-width:none; } }
`;

/**
 * A printable packing slip for an online order, in a window of its own: who it is for and where, what to put in the parcel (no prices), the buyer's note, and for cash on
 * delivery how much to collect. Prints on one page; the app's print styles show only this window.
 */
export default function PackingSlip({ orderId, onClose }) {
  const [slip, setSlip] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`/api/store/orders/${orderId}/packing-slip`).then((r) => setSlip(r.data)).catch((e) => setError(e.response?.data?.message || 'Could not load the packing slip.'));
  }, [orderId]);

  const money = (v) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <Modal show onHide={onClose} size="lg" className="print-modal" data-testid="packing-slip-dialog">
      <Modal.Header className="print-header">
        <Modal.Title>Packing slip</Modal.Title>
        <div className="print-actions">
          <Button variant="primary" className="me-2" onClick={() => window.print()} disabled={!slip} data-testid="print-packing-slip">Print</Button>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </Modal.Header>
      <Modal.Body className="print-body">
        <style>{STYLE}</style>
        {error && <Alert variant="warning">{error}</Alert>}
        {!slip && !error && <div className="text-center py-4"><Spinner animation="border" /></div>}
        {slip && (
          <div className="packing-slip" data-testid="packing-slip">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h2>PACKING SLIP</h2>
                <div>Order <b>{slip.orderNumber}</b>{slip.invoiceNumber ? <> &middot; invoice <b>{slip.invoiceNumber}</b></> : null}</div>
                <div className="ps-label">Placed {formatDateTime(slip.placedAt)}</div>
              </div>
              <div className="text-end">
                <div><b>{slip.seller}</b></div>
                {slip.sellerAddress && <div>{slip.sellerAddress}</div>}
                {slip.sellerPhone && <div>{slip.sellerPhone}</div>}
              </div>
            </div>

            <table style={{ marginTop: 12 }}>
              <tbody>
                <tr>
                  <td style={{ width: '60%' }}>
                    <div className="ps-label">Deliver to</div>
                    <div><b>{slip.buyerName}</b></div>
                    <div>{slip.addressLine}</div>
                    <div>{[slip.city, slip.state, slip.postalCode].filter(Boolean).join(', ')}</div>
                    <div>Phone: {slip.buyerPhone}</div>
                  </td>
                  <td>
                    <div className="ps-label">Sending</div>
                    <div>{slip.courierName ? `${slip.courierName}${slip.trackingNumber ? `, ${slip.trackingNumber}` : ''}` : 'Courier not chosen yet'}</div>
                    <div className="ps-label mt-2">Items</div>
                    <div><b>{slip.itemCount}</b> in {slip.lines.length} {slip.lines.length === 1 ? 'line' : 'lines'}</div>
                  </td>
                </tr>
              </tbody>
            </table>

            {slip.collectOnDelivery != null && <div className="ps-cod" data-testid="ps-cod">Cash on delivery: collect {money(slip.collectOnDelivery)}</div>}

            <Table bordered size="sm" className="mt-3 mb-2" style={{ color: '#111' }}>
              <thead><tr><th style={{ width: 36 }}>Packed</th><th>Item</th><th>SKU</th><th className="text-end">Qty</th></tr></thead>
              <tbody>
                {slip.lines.map((l, i) => (
                  <tr key={`${l.name}-${i}`}>
                    <td className="text-center"><span className="ps-box" /></td>
                    <td>{l.name}</td>
                    <td>{l.sku}</td>
                    <td className="text-end"><b>{l.quantity}</b></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {slip.buyerNote && <div><span className="ps-label">Buyer&apos;s note </span>{slip.buyerNote}</div>}
            <div className="mt-4 d-flex justify-content-between"><span>Packed by: ____________________</span><span>Checked by: ____________________</span></div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
